/**
 * Marketing attribution + tracking helpers.
 *
 * Captures UTM params on first landing, persists them to localStorage so every
 * subsequent form submission attaches the original ad-channel that drove the
 * visitor. Also exposes `trackEvent()` which fans out to Google Tag Manager
 * (dataLayer) + Meta Pixel (fbq) — GTM/Pixel snippets live in `public/index.html`.
 *
 * SECURITY NOTE: `localStorage` here holds ONLY non-PII marketing dimensions
 * (utm_source / referrer / first_seen_at). No tokens, no email, no name.
 * Safe by design — do not migrate to HttpOnly cookies (server needs to be
 * unaware of these values until form submit).
 */

const STORAGE_KEY = "toa_attribution_v1";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

function safeReferrer() {
  try {
    if (!document.referrer) return "";
    const url = new URL(document.referrer);
    if (url.host === window.location.host) return "";
    return document.referrer;
  } catch {
    return "";
  }
}

/**
 * Run once on app boot. Captures FIRST-TOUCH (never overwritten) + LAST-TOUCH
 * (updated on every attributable visit) attribution across sessions.
 *   - first_touch_*  → written ONLY when the bundle is created for the first time
 *   - last_touch_*   → updated whenever this visit carries fresh UTM params
 *                       OR the visitor arrives from an external referrer
 */
export function captureAttribution() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const incoming = {};
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) incoming[k] = v.slice(0, 200);
    });
    const gclid = params.get("gclid");
    const fbclid = params.get("fbclid");
    if (gclid) incoming.gclid = gclid.slice(0, 200);
    if (fbclid) incoming.fbclid = fbclid.slice(0, 200);

    const referrer = safeReferrer();
    const nowIso = new Date().toISOString();
    const landing = window.location.pathname + window.location.search;
    const existing = readAttribution();

    const hasIncomingUTMs = Object.keys(incoming).length > 0;
    const hasFreshTouch = hasIncomingUTMs || !!referrer;

    // First-touch — write ONLY if not already set. NEVER overwritten.
    let firstTouch;
    if (!existing || !existing.first_touch_at) {
      firstTouch = {
        first_touch_source:       incoming.utm_source || referrer || "direct",
        first_touch_medium:       incoming.utm_medium || (referrer ? "referral" : "direct"),
        first_touch_campaign:     incoming.utm_campaign || null,
        first_touch_landing_page: landing,
        first_touch_at:           nowIso,
      };
    } else {
      firstTouch = {
        first_touch_source:       existing.first_touch_source,
        first_touch_medium:       existing.first_touch_medium,
        first_touch_campaign:     existing.first_touch_campaign,
        first_touch_landing_page: existing.first_touch_landing_page,
        first_touch_at:           existing.first_touch_at,
      };
    }
    // Last-touch — updated only when a NEW attributable visit occurs;
    // otherwise carried forward from the previous bundle.
    let lastTouch;
    if (hasFreshTouch) {
      lastTouch = {
        last_touch_source:       incoming.utm_source || referrer || "direct",
        last_touch_medium:       incoming.utm_medium || (referrer ? "referral" : "direct"),
        last_touch_campaign:     incoming.utm_campaign || null,
        last_touch_landing_page: landing,
        last_touch_at:           nowIso,
      };
    } else if (existing) {
      lastTouch = {
        last_touch_source:       existing.last_touch_source       || existing.utm_source || "direct",
        last_touch_medium:       existing.last_touch_medium       || existing.utm_medium || null,
        last_touch_campaign:     existing.last_touch_campaign     || existing.utm_campaign || null,
        last_touch_landing_page: existing.last_touch_landing_page || existing.landing_page || landing,
        last_touch_at:           existing.last_touch_at           || existing.last_seen_at || nowIso,
      };
    } else {
      lastTouch = {
        last_touch_source: "direct", last_touch_medium: "direct",
        last_touch_campaign: null, last_touch_landing_page: landing, last_touch_at: nowIso,
      };
    }

    const bundle = {
      ...(existing || {}),
      ...incoming,
      // Latest-touch UTM copies kept flat for backward compat with existing lead/rfq docs
      landing_page: existing?.landing_page || landing,
      referrer: existing?.referrer || referrer,
      first_seen_at: existing?.first_seen_at || nowIso,
      last_seen_at: nowIso,
      ...firstTouch,
      ...lastTouch,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bundle));
    return bundle;
  } catch {
    return null;
  }
}

export function readAttribution() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Payload shape shipped with every form submission. Backend stores this on the
 * lead document so admins can see the ad-channel next to every enquiry.
 */
export function getAttributionPayload() {
  return readAttribution() || {};
}

/**
 * Consent gate — returns true only when the visitor has explicitly accepted
 * marketing tracking. Reads the same localStorage key ConsentBanner writes.
 */
function marketingConsent() {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem("toa_consent_v1");
    if (!raw) return false;
    const c = JSON.parse(raw);
    return c && c.marketing === true;
  } catch {
    return false;
  }
}

/**
 * Fire a marketing event to BOTH GTM dataLayer and Meta Pixel.
 * NO-OPS silently when marketing consent hasn't been granted — PIPEDA / Law 25.
 *
 * @param {string} name — event name, e.g. "catalog_download", "consultation_request"
 * @param {object} params — key/value payload (email, value, currency, source, …)
 */
export function trackEvent(name, params = {}) {
  if (typeof window === "undefined") return;
  if (!marketingConsent()) return;
  const attribution = getAttributionPayload();
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...params, attribution });
  } catch (e) {
    if (process.env.NODE_ENV === "development") console.debug("[attribution] dataLayer push failed:", e);
  }
  // Meta Pixel — map our internal event names → standard fbq events where sensible.
  try {
    if (typeof window.fbq === "function") {
      const META_MAP = {
        catalog_download: "Lead",
        consultation_request: "Schedule",
        contact_form: "Contact",
        quote_request: "Lead",
        trade_partner: "Lead",
      };
      const mapped = META_MAP[name];
      if (mapped) {
        window.fbq("track", mapped, { content_name: name, ...params });
      } else {
        window.fbq("trackCustom", name, params);
      }
    }
  } catch (e) {
    if (process.env.NODE_ENV === "development") console.debug("[attribution] fbq failed:", e);
  }
}
